import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { PublicUser } from '@runninglime/shared';
import { apiGet, apiPut, apiDelete } from '../../lib/api.ts';
import { LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';
import { formatDate } from '../../lib/utils.ts';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending_profile: { label: 'Pending Profile', color: 'bg-gray-200 text-gray-700' },
  pending_verification: { label: 'Pending Verification', color: 'bg-amber-100 text-amber-800' },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

export default function UserManager() {
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  // Reject modal state
  const [rejectingUser, setRejectingUser] = useState<PublicUser | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Delete confirmation
  const [deletingUser, setDeletingUser] = useState<PublicUser | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const res = await apiGet<{ items: PublicUser[]; total: number }>('/admin/users');
    if (res.success && res.data) {
      setUsers(res.data.items);
    }
    setLoading(false);
  };

  const handleVerify = async (userId: string) => {
    setActionLoading(userId);
    const res = await apiPut<PublicUser>(`/admin/users/${userId}/verify`);
    if (res.success) {
      await loadUsers();
    }
    setActionLoading(null);
  };

  const handleReject = async () => {
    if (!rejectingUser || !rejectReason.trim()) return;
    setActionLoading(rejectingUser.id);
    const res = await apiPut<PublicUser>(`/admin/users/${rejectingUser.id}/reject`, {
      reason: rejectReason,
    });
    if (res.success) {
      await loadUsers();
    }
    setRejectingUser(null);
    setRejectReason('');
    setActionLoading(null);
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setActionLoading(deletingUser.id);
    const res = await apiDelete(`/admin/users/${deletingUser.id}`);
    if (res.success) {
      await loadUsers();
    }
    setDeletingUser(null);
    setActionLoading(null);
  };

  if (loading) return <LoadingSpinner />;

  const filteredUsers = users.filter((u) => {
    if (u.role === 'admin') return filter === 'all';
    if (u.role === 'employee') return filter === 'all' || filter === 'verified';
    if (filter === 'all') return true;
    return u.verificationStatus === filter;
  });

  const pendingCount = users.filter((u) => u.verificationStatus === 'pending_verification').length;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dark">User Management</h1>
          <p className="text-gray-500 mt-1">{users.length} users total</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-xl font-medium text-sm">
              {pendingCount} pending verification
            </div>
          )}
          <Link
            to="/admin/enrollment"
            className="btn-gradient px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Enroll User
          </Link>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'all', label: 'All' },
          { key: 'pending_verification', label: 'Pending Verification' },
          { key: 'verified', label: 'Verified' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'pending_profile', label: 'Pending Profile' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              filter === tab.key
                ? 'bg-lime/30 text-dark'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {filteredUsers.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No users found"
          description="No users match the selected filter."
        />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">VAT / SDI</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Shipping Address</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Registered</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredUsers.map((u) => {
                  const statusInfo = u.role === 'admin'
                    ? { label: 'Admin', color: 'bg-purple-100 text-purple-800' }
                    : u.role === 'employee'
                    ? { label: 'Employee', color: 'bg-indigo-100 text-indigo-800' }
                    : STATUS_LABELS[u.verificationStatus || 'pending_profile'];
                  const isCustomer = u.role === 'customer';
                  const isEmployee = u.role === 'employee';

                  return (
                    <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-dark">{u.contactName}</div>
                        <div className="text-sm text-gray-500">{u.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{u.companyName}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {u.verificationStatus === 'rejected' && u.rejectionReason && (
                          <p className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={u.rejectionReason}>
                            {u.rejectionReason}
                          </p>
                        )}
                        {isEmployee && u.permissions && u.permissions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {u.permissions.map((p) => (
                              <span key={p} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px] font-medium">
                                {p.replace('admin:', '')}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.billingProfile ? (
                          <div>
                            <div>{u.billingProfile.vatNumber}</div>
                            <div className="text-gray-400">{u.billingProfile.sdiNumber}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.billingProfile?.shippingAddress ? (
                          <div className="max-w-[220px]">
                            <div>{u.billingProfile.shippingAddress.street}</div>
                            <div className="text-gray-400">
                              {u.billingProfile.shippingAddress.postalCode} {u.billingProfile.shippingAddress.city} ({u.billingProfile.shippingAddress.province})
                            </div>
                            <div className="text-gray-400">{u.billingProfile.shippingAddress.country}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(u.createdAt)}</td>
                      <td className="px-6 py-4 text-right">
                        {(isCustomer || isEmployee) && (
                          <div className="flex items-center justify-end gap-2">
                            {(u.verificationStatus === 'pending_verification' || u.verificationStatus === 'rejected') && (
                              <button
                                onClick={() => handleVerify(u.id)}
                                disabled={actionLoading === u.id}
                                className="px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                              >
                                Verify
                              </button>
                            )}
                            {(u.verificationStatus === 'pending_verification' || u.verificationStatus === 'verified') && (
                              <button
                                onClick={() => { setRejectingUser(u); setRejectReason(''); }}
                                disabled={actionLoading === u.id}
                                className="px-3 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                              >
                                Reject
                              </button>
                            )}
                            <button
                              onClick={() => setDeletingUser(u)}
                              disabled={actionLoading === u.id}
                              className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingUser && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
            <h3 className="text-lg font-bold text-dark mb-2">Reject User</h3>
            <p className="text-gray-500 text-sm mb-4">
              Rejecting <strong>{rejectingUser.contactName}</strong> ({rejectingUser.companyName}) will lock them into the billing profile page with the reason you specify.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (visible to the user)..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-300 focus:border-transparent outline-none transition-all resize-none h-28"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectingUser(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading === rejectingUser.id}
                className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 font-medium text-sm transition-colors disabled:opacity-50"
              >
                Reject User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
            <h3 className="text-lg font-bold text-red-700 mb-2">Delete User</h3>
            <p className="text-gray-600 text-sm mb-1">
              Are you sure you want to permanently delete <strong>{deletingUser.contactName}</strong> ({deletingUser.companyName})?
            </p>
            <p className="text-red-600 text-sm mb-4">
              This will also delete all their orders and support tickets. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingUser(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading === deletingUser.id}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium text-sm transition-colors disabled:opacity-50"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
